import type { ReactNode } from 'react';
import { AlertIcon, CheckCircleIcon, CloseIcon, InfoIcon } from '@/components/icons';
import { cn } from '@/lib/cn';

export type AlertTone = 'success' | 'warning' | 'info' | 'error';

const TONES: Record<AlertTone, { box: string; icon: string; close: string }> = {
  success: {
    box: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    icon: 'text-emerald-600',
    close: 'text-emerald-700 hover:bg-emerald-100',
  },
  warning: {
    box: 'border-amber-200 bg-amber-50 text-amber-900',
    icon: 'text-amber-600',
    close: 'text-amber-800 hover:bg-amber-100',
  },
  info: {
    box: 'border-niteroi-100 bg-niteroi-50 text-niteroi-900',
    icon: 'text-niteroi-500',
    close: 'text-niteroi-700 hover:bg-niteroi-100',
  },
  error: {
    box: 'border-red-200 bg-red-50 text-red-900',
    icon: 'text-red-600',
    close: 'text-red-700 hover:bg-red-100',
  },
};

interface AlertProps {
  tone?: AlertTone;
  children: ReactNode;
  /** Ícone próprio no lugar do padrão do tom (ex.: cadeado para processo sigiloso). */
  icon?: ReactNode;
  /** Mostra o "x" de fechar. */
  onDismiss?: () => void;
  /**
   * `status` para confirmações (lidas sem interromper) e `alert` para erros (lidos na hora).
   * Sem papel, o aviso é texto comum — use quando ele já está na tela ao abrir a página.
   */
  role?: 'status' | 'alert';
  className?: string;
}

/**
 * Faixa de aviso: confirmação (verde), atenção (âmbar), informação (azul) ou erro (vermelho).
 * O ícone acompanha a cor — cor sozinha não avisa quem não a distingue.
 */
export function Alert({ tone = 'info', children, icon, onDismiss, role, className }: AlertProps) {
  const t = TONES[tone];
  const Icone = tone === 'success' ? CheckCircleIcon : tone === 'info' ? InfoIcon : AlertIcon;
  return (
    <div
      role={role}
      className={cn('flex items-start gap-3 rounded-lg border p-3 text-sm', t.box, className)}
    >
      <span className={cn('mt-0.5 shrink-0', t.icon)}>{icon ?? <Icone />}</span>
      <div className="min-w-0 flex-1 break-words">{children}</div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Fechar"
          className={cn(
            '-m-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
            t.close,
          )}
        >
          <CloseIcon />
        </button>
      )}
    </div>
  );
}
